import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, RefreshCw, Calendar, CreditCard, Bell, Check,
  Trash2, AlertCircle, Clock, TrendingUp, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useLanguage } from '../context/LanguageContext';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import api from '../lib/api';
import { useTutorial } from '../components/OnboardingTutorial';

const OnboardingTutorial = lazy(() => import('../components/OnboardingTutorial'));

const CATEGORY_COLORS = {
  logement: '#FF4F00',
  transport: '#3B82F6',
  alimentation: '#10B981',
  abonnements: '#8B5CF6',
  shopping: '#EC4899',
  loisirs: '#F59E0B',
  sante: '#EF4444',
  divers: '#A8A29E',
};

const CATEGORY_NAMES_FR = {
  logement: 'Logement',
  transport: 'Transport',
  alimentation: 'Alimentation',
  abonnements: 'Abonnements',
  shopping: 'Shopping',
  loisirs: 'Loisirs',
  sante: 'Santé',
  divers: 'Divers',
};

export default function RecurringPage() {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { shouldShow: showTutorial } = useTutorial();
  
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [detectedRecurring, setDetectedRecurring] = useState([]);
  const [confirmedRecurring, setConfirmedRecurring] = useState([]);
  const [upcomingPayments, setUpcomingPayments] = useState([]);
  const [totalMonthly, setTotalMonthly] = useState(0);
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [tutorialActive, setTutorialActive] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // Show tutorial after component is mounted
  useEffect(() => {
    if (showTutorial) {
      const timer = setTimeout(() => setTutorialActive(true), 500);
      return () => clearTimeout(timer);
    }
  }, [showTutorial]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load confirmed recurring and detect new patterns
      const [confirmedRes, detectedRes] = await Promise.all([
        api.get('/recurring'),
        api.get('/recurring/detect')
      ]);
      
      setConfirmedRecurring(confirmedRes.data.recurring || []);
      setUpcomingPayments(confirmedRes.data.upcoming_payments || []);
      setTotalMonthly(confirmedRes.data.total_monthly || 0);
      
      // Filter out already confirmed patterns
      const confirmedPatterns = new Set(
        (confirmedRes.data.recurring || []).map(r => r.description_pattern)
      );
      
      const newDetected = (detectedRes.data.recurring || []).filter(
        r => !confirmedPatterns.has(r.pattern)
      );
      
      setDetectedRecurring(newDetected);
      
    } catch (error) {
      console.error('Failed to load recurring data:', error);
      toast.error(t('loading_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDetect = async () => {
    setDetecting(true);
    try {
      const res = await api.get('/recurring/detect');
      
      // Filter out already confirmed
      const confirmedPatterns = new Set(confirmedRecurring.map(r => r.description_pattern));
      const newDetected = (res.data.recurring || []).filter(
        r => !confirmedPatterns.has(r.pattern)
      );
      
      setDetectedRecurring(newDetected);
      toast.success(t('patterns_detected_count').replace('{count}', newDetected.length));
    } catch (error) {
      toast.error(t('detection_error'));
    } finally {
      setDetecting(false);
    }
  };

  const handleConfirm = async (pattern) => {
    try {
      await api.post('/recurring/confirm', {
        pattern: pattern.pattern,
        description: pattern.description,
        category: pattern.category,
        average_amount: pattern.average_amount,
        frequency: pattern.frequency,
        next_expected: pattern.next_expected
      });
      
      toast.success(t('recurring_confirmed'));
      setSelectedPattern(null);
      loadData();
    } catch (error) {
      toast.error(t('confirmation_error'));
    }
  };

  const handleDelete = async (recurringId) => {
    try {
      await api.delete(`/recurring/${recurringId}`);
      toast.success(t('deleted'));
      loadData();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : language === 'pt' ? 'pt-PT' : 'en-US', {
      day: 'numeric',
      month: 'short'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-bold text-lg">
                  {t('recurring_expenses')}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {t('subscriptions_payments')}
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleDetect} disabled={detecting} data-testid="detect-recurring-btn">
              {detecting ? (
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              {t('detect')}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Summary Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm">
                    {t('monthly_recurring_total')}
                  </p>
                  <p className="text-4xl font-bold">{formatCurrency(totalMonthly)}</p>
                  <p className="text-white/70 text-sm mt-1">
                    {confirmedRecurring.length} {t('active_subscriptions')}
                  </p>
                </div>
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                  <CreditCard className="w-8 h-8" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Upcoming Payments */}
        {upcomingPayments.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Bell className="w-5 h-5 text-amber-500" />
                  {t('upcoming_payments')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {upcomingPayments.map((payment, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-950/20 rounded-xl"
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: CATEGORY_COLORS[payment.category] || '#A8A29E' }}
                      >
                        <Clock className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium">{payment.description_pattern}</p>
                        <p className="text-xs text-muted-foreground">
                          {t('in_days')} {payment.days_until} {t('days')}
                        </p>
                      </div>
                    </div>
                    <p className="font-bold text-amber-600">{formatCurrency(payment.average_amount)}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Detected Patterns */}
        {detectedRecurring.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-blue-500" />
                  {t('detected_patterns')}
                  <span className="ml-auto text-sm font-normal bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    {detectedRecurring.length}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {detectedRecurring.map((pattern, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-4 bg-muted/50 rounded-xl cursor-pointer hover:bg-muted transition-colors"
                    onClick={() => setSelectedPattern(pattern)}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: CATEGORY_COLORS[pattern.category] || '#A8A29E' }}
                      >
                        <TrendingUp className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium">{pattern.description}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                            {pattern.frequency_fr}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {pattern.occurrences}x {t('detected')}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{formatCurrency(pattern.average_amount)}</p>
                      <p className="text-xs text-muted-foreground">
                        {Math.round(pattern.confidence * 100)}% {t('confidence')}
                      </p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Confirmed Recurring */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                {t('confirmed_subscriptions')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {confirmedRecurring.length > 0 ? (
                <div className="space-y-3">
                  {confirmedRecurring.map((rec, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-950/20 rounded-xl"
                    >
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: CATEGORY_COLORS[rec.category] || '#A8A29E' }}
                        >
                          <CreditCard className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-medium">{rec.description_pattern}</p>
                          <p className="text-xs text-muted-foreground">
                            {t(`cat_${rec.category}`) || rec.category} • {
                              rec.frequency === 'monthly' ? t('monthly_freq') :
                              rec.frequency === 'weekly' ? t('weekly_freq') :
                              rec.frequency === 'yearly' ? t('yearly_freq') :
                              rec.frequency
                            }
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="font-bold">{formatCurrency(rec.average_amount)}</p>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDelete(rec.recurring_id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CreditCard className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="font-medium">
                    {t('no_confirmed_subscriptions')}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('click_detect_analyze')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* How it works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="bg-muted/30">
            <CardContent className="p-6">
              <h3 className="font-bold mb-4">
                {t('how_it_works')}
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">1</div>
                  <p>{t('spenspot_analyzes_6_months')}</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">2</div>
                  <p>{t('patterns_auto_detected')}</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">3</div>
                  <p>{t('confirm_subs_for_reminders')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </main>

      {/* Confirm Pattern Dialog */}
      <Dialog open={!!selectedPattern} onOpenChange={() => setSelectedPattern(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('confirm_recurring_expense')}
            </DialogTitle>
          </DialogHeader>
          
          {selectedPattern && (
            <div className="space-y-4 py-4">
              <div className="bg-muted rounded-lg p-4">
                <p className="font-bold text-lg">{selectedPattern.description}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-2xl font-bold text-primary">
                    {formatCurrency(selectedPattern.average_amount)}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    / {selectedPattern.frequency_fr?.toLowerCase()}
                  </span>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('category')}</span>
                  <span>{t(`cat_${selectedPattern.category}`) || selectedPattern.category}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('occurrences')}</span>
                  <span>{selectedPattern.occurrences}x</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('next_expected_date')}</span>
                  <span>{formatDate(selectedPattern.next_expected)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('confidence')}</span>
                  <span>{Math.round(selectedPattern.confidence * 100)}%</span>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedPattern(null)}>
              {t('cancel')}
            </Button>
            <Button onClick={() => selectedPattern && handleConfirm(selectedPattern)}>
              <Check className="w-4 h-4 mr-2" />
              {t('confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Onboarding Tutorial */}
      <Suspense fallback={null}>
        {tutorialActive && (
          <OnboardingTutorial onComplete={() => setTutorialActive(false)} />
        )}
      </Suspense>
    </div>
  );
}
